import { useMemo, useState } from 'react';
import { doc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import type { z } from 'zod';
import type { SceneCategory, StoryTemplateDoc } from '@spirits/shared';
import { db } from '../firebase';
import {
  CATEGORIES,
  castSchema,
  scenesSchema,
  endingsSchema,
  canonSchema,
  parseBlock,
  toJson,
  blankTemplate,
  type Block,
} from './templateSchema';

type Existing = StoryTemplateDoc & { id: string };

const ID_RE = /^[a-z0-9][a-z0-9-]*$/;

// Serialize an existing template doc into the editor's per-block JSON strings.
function fromExisting(t: Existing) {
  return {
    id: t.id,
    title: t.title ?? '',
    category: (t.category ?? 'fiction') as SceneCategory,
    blurb: t.blurb ?? '',
    canonText: t.canon ? toJson(t.canon) : '',
    castText: toJson(t.cast ?? []),
    scenesText: toJson(t.scenes ?? []),
    endingsText: toJson(t.endings ?? []),
  };
}

export function TemplateEditor({
  existing,
  onClose,
}: {
  existing: Existing | null;
  onClose: () => void;
}) {
  const init = useMemo(() => (existing ? fromExisting(existing) : blankTemplate()), [existing]);
  const isNew = !existing;

  const [id, setId] = useState(init.id);
  const [title, setTitle] = useState(init.title);
  const [category, setCategory] = useState<SceneCategory>(init.category);
  const [blurb, setBlurb] = useState(init.blurb);
  const [canonText, setCanonText] = useState(init.canonText);
  const [castText, setCastText] = useState(init.castText);
  const [scenesText, setScenesText] = useState(init.scenesText);
  const [endingsText, setEndingsText] = useState(init.endingsText);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [activeTab, setActiveTab] = useState<Block>('cast');

  const idValid = ID_RE.test(id);

  // Validity of all four JSON blocks, so the tabs can flag errors even when the
  // block isn't the active one.
  const statuses = useMemo(
    () => ({
      cast: blockStatus(castText, castSchema, false),
      scenes: blockStatus(scenesText, scenesSchema, false),
      endings: blockStatus(endingsText, endingsSchema, false),
      canon: blockStatus(canonText, canonSchema, true),
    }),
    [castText, scenesText, endingsText, canonText],
  );

  const tabs: { key: Block; label: string; hint: string; value: string; onChange: (v: string) => void }[] = [
    {
      key: 'cast',
      label: 'Cast',
      hint: 'StoryCharacter[] — identity + arcs for each character.',
      value: castText,
      onChange: setCastText,
    },
    {
      key: 'scenes',
      label: 'Scenes',
      hint: 'TemplateScene[] — the authored spine. Scene 0 is the full opening (with bots); the rest are skeletons (name + stage + instruction).',
      value: scenesText,
      onChange: setScenesText,
    },
    {
      key: 'endings',
      label: 'Endings',
      hint: 'StoryEnding[] — the poles the arcs vote between. requiredArcs maps characterKey → arcId.',
      value: endingsText,
      onChange: setEndingsText,
    },
    {
      key: 'canon',
      label: 'Canon',
      hint: 'StoryCanon — how the original really went (recap-only). Leave blank for wholly-original stories.',
      value: canonText,
      onChange: setCanonText,
    },
  ];
  const active = tabs.find((t) => t.key === activeTab)!;

  async function save() {
    if (saving) return;
    setSaveError(null);

    if (!idValid) {
      setSaveError('Template id must be a slug: lowercase letters, numbers, and dashes.');
      return;
    }
    if (!title.trim()) {
      setSaveError('Title is required.');
      return;
    }

    const cast = parseBlock(castText, castSchema);
    const scenes = parseBlock(scenesText, scenesSchema);
    const endings = parseBlock(endingsText, endingsSchema);
    const canonTrimmed = canonText.trim();
    const canon = canonTrimmed
      ? parseBlock(canonText, canonSchema)
      : ({ ok: true, value: undefined } as const);

    const firstError =
      (!cast.ok && `Cast — ${cast.error}`) ||
      (!scenes.ok && `Scenes — ${scenes.error}`) ||
      (!endings.ok && `Endings — ${endings.error}`) ||
      (!canon.ok && `Canon — ${canon.error}`);
    if (firstError) {
      setSaveError(firstError);
      return;
    }

    setSaving(true);
    try {
      const docData: Record<string, unknown> = {
        schemaVersion: 1,
        title: title.trim(),
        category,
        blurb: blurb.trim(),
        endings: (endings as { value: unknown }).value,
        cast: (cast as { value: unknown }).value,
        scenes: (scenes as { value: unknown }).value,
        seedKey: existing?.seedKey ?? 'admin',
        createdAt: existing?.createdAt ?? serverTimestamp(),
      };
      const canonValue = (canon as { value: unknown }).value;
      if (canonValue) docData.canon = canonValue;

      await setDoc(doc(db, 'storyTemplates', id), docData);
      onClose();
    } catch (e) {
      setSaveError((e as Error)?.message ?? 'save failed');
      setSaving(false);
    }
  }

  async function remove() {
    if (!existing) return;
    setSaving(true);
    setSaveError(null);
    try {
      await deleteDoc(doc(db, 'storyTemplates', existing.id));
      onClose();
    } catch (e) {
      setSaveError((e as Error)?.message ?? 'delete failed');
      setSaving(false);
    }
  }

  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5">
      <div className="flex items-center justify-between gap-4 mb-4">
        <h2 className="text-lg text-slate-100">
          {isNew ? 'New template' : `Edit: ${existing!.title}`}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="text-sm text-slate-400 hover:text-slate-200"
        >
          ← back to list
        </button>
      </div>

      {/* Scalars */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-xs uppercase tracking-widest text-slate-400">Template id (doc key)</span>
          <input
            type="text"
            value={id}
            disabled={!isNew}
            onChange={(e) => setId(e.target.value)}
            placeholder="three-little-pigs"
            className={`mt-1 w-full rounded-lg border bg-slate-950 px-3 py-2 text-sm text-slate-100 font-mono placeholder:text-slate-600 focus:outline-none disabled:opacity-60 ${
              isNew && !idValid && id ? 'border-rose-600/60' : 'border-slate-700 focus:border-amber-400/60'
            }`}
          />
          {isNew ? (
            <span className="mt-1 block text-[11px] text-slate-500">
              Lowercase slug — letters, numbers, dashes. Becomes the Firestore doc id.
            </span>
          ) : (
            <span className="mt-1 block text-[11px] text-slate-500">Fixed — id can't be changed.</span>
          )}
        </label>

        <label className="block">
          <span className="text-xs uppercase tracking-widest text-slate-400">Category</span>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as SceneCategory)}
            className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-amber-400/60"
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>

        <label className="block sm:col-span-2">
          <span className="text-xs uppercase tracking-widest text-slate-400">Title</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-amber-400/60"
          />
        </label>

        <label className="block sm:col-span-2">
          <span className="text-xs uppercase tracking-widest text-slate-400">Blurb (catalog one-liner)</span>
          <textarea
            value={blurb}
            onChange={(e) => setBlurb(e.target.value)}
            rows={2}
            className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-amber-400/60 resize-y"
          />
        </label>
      </div>

      {/* JSON blocks — one tab each, so each editor gets the full height. */}
      <div className="mt-6">
        <div className="flex flex-wrap gap-1 border-b border-slate-800">
          {tabs.map((t) => {
            const isActive = t.key === activeTab;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setActiveTab(t.key)}
                className={`-mb-px flex items-center gap-1.5 rounded-t-lg border-b-2 px-3 py-1.5 text-sm transition-colors ${
                  isActive
                    ? 'border-amber-400/70 text-amber-100'
                    : 'border-transparent text-slate-400 hover:text-slate-200'
                }`}
              >
                {t.label}
                <StatusDot status={statuses[t.key]} />
              </button>
            );
          })}
        </div>
        <div className="pt-3">
          <JsonBlock
            key={active.key}
            hint={active.hint}
            value={active.value}
            onChange={active.onChange}
            status={statuses[active.key]}
          />
        </div>
      </div>

      {saveError && (
        <pre className="mt-4 whitespace-pre-wrap rounded-lg border border-rose-700/50 bg-rose-950/30 px-3 py-2 text-xs text-rose-300">
          {saveError}
        </pre>
      )}

      <div className="mt-5 flex items-center justify-between gap-3">
        <div>
          {existing &&
            (confirmDelete ? (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-rose-300">Delete “{existing.title}”?</span>
                <button
                  type="button"
                  onClick={remove}
                  disabled={saving}
                  className="px-3 py-1.5 rounded-lg border border-rose-700/60 text-rose-300 hover:bg-rose-900/30 disabled:opacity-50"
                >
                  yes, delete
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDelete(false)}
                  className="px-3 py-1.5 rounded-lg border border-slate-700 text-slate-400 hover:text-slate-200"
                >
                  cancel
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                className="px-3 py-1.5 rounded-lg border border-rose-900/60 text-rose-400/80 hover:text-rose-300 hover:border-rose-700/60 text-sm"
              >
                Delete template
              </button>
            ))}
        </div>
        <div className="flex items-center gap-3 text-sm">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 rounded-lg border border-slate-700 text-slate-400 hover:text-slate-200"
          >
            cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="px-4 py-1.5 rounded-lg border border-amber-400/60 text-amber-200 bg-amber-900/30 hover:bg-amber-900/50 disabled:opacity-50"
          >
            {saving ? 'saving…' : isNew ? 'Create template' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

type BlockStatus =
  | { kind: 'valid' }
  | { kind: 'empty' }
  | { kind: 'invalid'; error: string };

// Validate a block's JSON text against its schema. An optional block that's blank
// is 'empty' (allowed), not invalid.
function blockStatus(value: string, schema: z.ZodType<unknown>, optional: boolean): BlockStatus {
  if (optional && value.trim() === '') return { kind: 'empty' };
  const res = parseBlock(value, schema);
  return res.ok ? { kind: 'valid' } : { kind: 'invalid', error: res.error };
}

function StatusDot({ status }: { status: BlockStatus }) {
  if (status.kind === 'valid') return <span className="text-xs text-emerald-400">✓</span>;
  if (status.kind === 'invalid') return <span className="text-xs text-rose-400">✗</span>;
  return <span className="text-xs text-slate-600">—</span>;
}

function JsonBlock({
  hint,
  value,
  onChange,
  status,
}: {
  hint: string;
  value: string;
  onChange: (v: string) => void;
  status: BlockStatus;
}) {
  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <p className="text-[11px] text-slate-500">{hint}</p>
        {status.kind === 'valid' && <span className="flex-none text-xs text-emerald-400">✓ valid</span>}
        {status.kind === 'empty' && <span className="flex-none text-xs text-slate-500">— empty</span>}
        {status.kind === 'invalid' && <span className="flex-none text-xs text-rose-400">✗ invalid</span>}
      </div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        spellCheck={false}
        rows={22}
        className={`mt-1.5 w-full rounded-lg border bg-slate-950 px-3 py-2 text-xs font-mono leading-relaxed text-slate-200 focus:outline-none resize-y ${
          status.kind === 'invalid' ? 'border-rose-700/50 focus:border-rose-600' : 'border-slate-700 focus:border-amber-400/60'
        }`}
      />
      {status.kind === 'invalid' && (
        <pre className="mt-1 whitespace-pre-wrap text-[11px] text-rose-400/90">{status.error}</pre>
      )}
    </div>
  );
}
